import {
  LineOfCreditTransactionView,
  VlocPaymentSchedule,
} from '@varo/graphql-schema-types';
import { format, parseISO } from 'date-fns';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import LineOfCreditErrorScreenComponent, {
  VlocStepName,
} from 'features/lineOfCredit/screens/error/LineOfCreditErrorScreenComponent';
import { storePaymentSchedule } from 'features/lineOfCredit/screens/paymentSchedule/paymentScheduleRouteStore';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import { amountStyles } from 'shared/components/vxl/TransactionListItem/TransactionListItem';
import ExpressiveLgText from 'shared/components/vxl/typography/expressive/ExpressiveLgText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import SectionedContentScreen, {
  SectionedContentScreenModel,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponent, VXLComponentId, VXLNode } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

import useVlocHistoryDetails from './hooks/useVlocHistoryDetails';

const VlocHistoryDetails = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('vloc', {
    keyPrefix: 'historyDetails',
  });

  const { bottom } = useSafeAreaInsets();

  const { vlocData, loading = false, errorModel } = useVlocHistoryDetails(id);

  const sectionsData: SectionedContentScreenModel = { sections: [] };
  const reviewDetailItems: VXLComponent[] = [];

  const {
    lineOfCreditDetails: { items = [], titleAmount = '', amount = '' } = {},
    transactions = [],
  } = vlocData || {};

  const onReviewItemPressed = (paymentScheduleInfo: VlocPaymentSchedule[]) => {
    // For user coming from history details we can assume that the remaining amount is $0.00
    router.navigate({
      pathname: '/varo-line-of-credit/payment-schedule',
      params: {
        paymentScheduleId: storePaymentSchedule(paymentScheduleInfo),
        totalAmount: '$0.00',
        paidToDate: amount,
      },
    });
  };

  items?.map((item) => {
    reviewDetailItems.push({
      id: item.id,
      name: VXLComponentId.LIST_ITEM,
      props: {
        primaryCopy: item.title,
        lineFormat: 'LABEL',
        showsChevron: item.isClickable,
        analyticsId: 'paymentScheduleClicked',
        trailingAccessory: {
          id: item.id,
          name: VXLComponentId.LIST_ITEM_TEXT_STACK,
          props: {
            primaryCopy: item.description,
          },
        },
        onPress: () => {
          if (item.isClickable) {
            onReviewItemPressed(item.paymentSchedule || []);
          }
        },
      },
    });
  });

  sectionsData.sections.push({
    sectionHeader: undefined,
    data: reviewDetailItems,
  });

  let transactionItems: VXLComponent[] = [];
  let transactionHeader = {} as VXLNode;

  const groupedMonthlyTransactions = transactions.reduce<
    Record<string, LineOfCreditTransactionView[]>
  >((prevTransaction, transaction) => {
    const monthYear = format(parseISO(transaction.date ?? ''), 'MMMM yyyy');
    if (!prevTransaction[monthYear]) {
      prevTransaction[monthYear] = [];
    }
    prevTransaction[monthYear].push(transaction as LineOfCreditTransactionView);
    return prevTransaction;
  }, {});

  Object.entries(groupedMonthlyTransactions).map(([key, value]) => {
    transactionHeader = {
      id: `vloc-history-detail-section-header-${key}`,
      name: VXLComponentId.SECTION_DIVIDER,
      props: {
        labelText: key,
      },
    };
    value?.map((transaction, index) => {
      transactionItems.push({
        id: `vloc-history-detail-item-${index}`,
        name: VXLComponentId.TRANSACTION_LIST_ITEM,
        props: {
          isPending: false,
          titleText: transaction?.description,
          amountText: transaction.amount?.formattedWithPlusPrefix,
          balanceText: transaction?.runningBalance?.formattedWithPlusPrefix,
          amountStyle: transaction?.accountFrom
            ? amountStyles.NEUTRAL
            : amountStyles.POSITIVE,
          secondaryText: transaction.date
            ? format(parseISO(transaction.date), 'MMMM dd')
            : '',
          onPress: () =>
            router.navigate({
              pathname: '/varo-line-of-credit/transaction-details',
              params: {
                amount: transaction.amount?.formattedWithPlusPrefix,
                accountFrom: transaction.accountFrom,
                accountTo: transaction.accountTo,
                date: transaction.date,
                description: transaction.description,
                status: transaction.status,
              },
            }),
        },
      });
    });
    sectionsData.sections.push({
      sectionHeader: transactionHeader,
      data: transactionItems,
    });
    transactionItems = [];
  });

  const showError = errorModel && !loading;

  const dismissToTab = useDismissToRootTab();

  const goHome = () => {
    dismissToTab(Tabs.HOME);
  };

  const goBack = () => {
    router.back();
  };

  useBackHandler(showError ? goHome : goBack);

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      <Stack.Screen
        options={{
          headerBackVisible: !showError,
          headerShown: !showError,
          gestureEnabled: !showError,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      <TitleLgText style={styles.titleLgText} numberOfLines={0}>
        {translatedText('title')}
      </TitleLgText>

      <View>
        <BodySmText style={styles.titleSmText} numberOfLines={0}>
          {titleAmount}
        </BodySmText>
        <ExpressiveLgText style={styles.titleLgText} numberOfLines={0}>
          {amount}
        </ExpressiveLgText>
      </View>

      <SectionedContentScreen model={sectionsData} />

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel ? (
            <LineOfCreditErrorScreenComponent
              screenAnalyticsId="/varo-line-of-credit/LineOfCreditErrorScreenComponent-vlocHistoryDetails"
              trackingAnalyticsId="manage-account-error-screen-tracking"
              trackingCustomAttributes={{
                step_name: VlocStepName.VLOC_HISTORY_DETAILS,
              }}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  titleLgText: {
    marginHorizontal: theme.spacing.m,
  },
  titleSmText: {
    color: theme.color.text.secondary,
    marginHorizontal: theme.spacing.m,
    marginTop: theme.spacing.l,
  },
});

export default VlocHistoryDetails;
